import ApplicationController from './application_controller'
import Logger from '../lib/logger'

/**
 * InfiniteScrollController - Reusable Infinite Scroll Implementation
 * 
 * A Stimulus controller that provides infinite scrolling functionality using the
 * Intersection Observer API. Automatically loads more content when the user
 * scrolls near the bottom of a container.
 * 
 * Key Features:
 * - Uses Intersection Observer for optimal performance
 * - Configurable threshold and root margin
 * - Loading state management
 * - Error handling with retry logic
 * - Emits custom events for integration
 * - Supports both automatic and manual loading
 * 
 * Usage:
 * <div data-controller="infinite-scroll" 
 *      data-infinite-scroll-url-value="/api/items"
 *      data-infinite-scroll-per-page-value="25">
 *   <div data-infinite-scroll-target="entries">
 *     <!-- Content items go here -->
 *   </div>
 *   <div data-infinite-scroll-target="sentinel"></div>
 *   <div data-infinite-scroll-target="loading" class="d-none">
 *     <!-- Loading spinner -->
 *   </div>
 * </div>
 * 
 * @class InfiniteScrollController
 */
export default class extends ApplicationController {
  static targets = ['entries', 'sentinel', 'loading']
  static values = { 
    url: String,
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 25 },
    hasMore: { type: Boolean, default: true },
    threshold: { type: Number, default: 0.1 },
    rootMargin: { type: String, default: '100px' },
    loading: { type: Boolean, default: false }
  }

  // Module-level Set of elements with an in-flight load request.
  // Shared reference prevents race conditions where multiple IntersectionObserver
  // entries for the same element trigger separate loads before loadingValue updates.
  static _loadingElements = new WeakSet()

  connect() {
    Logger.debug('Infinite scroll controller connected', { controller: this.constructor.name })
    
    // Listen for reinitialize event
    this.element.addEventListener('infinite-scroll:reinitialize', this.handleReinitialize.bind(this))
    
    // Only setup observer if we have a URL (content is loaded)
    if (this.urlValue) {
      this.setupIntersectionObserver()
    }
    this.dispatch('connected', { detail: { controller: this } })
  }

  /**
   * Handle reinitialize event triggered by external code
   */
  handleReinitialize(event) {
    Logger.debug('Received reinitialize event', { controller: this.constructor.name })
    this.reinitialize()
  }

  /**
   * Reinitialize the controller after content is dynamically loaded
   * Call this method after updating the content and setting new values
   */
  reinitialize() {
    Logger.debug('Reinitializing infinite scroll controller', { controller: this.constructor.name })
    
    // Cleanup existing observer
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    
    // Setup new observer with current targets
    this.setupIntersectionObserver()
  }

  disconnect() {
    Logger.debug('Infinite scroll controller disconnected', { controller: this.constructor.name })
    this.cleanup()
    this.dispatch('disconnected', { detail: { controller: this } })
  }

  /**
   * Setup Intersection Observer to watch for sentinel element
   */
  setupIntersectionObserver() {
    if (!this.hasSentinelTarget) {
      Logger.warn('No sentinel target found for infinite scroll', { controller: this.constructor.name })
      return
    }

    // Find the scrollable container (important for fixed-height containers like tabs)
    const scrollContainer = this.findScrollableAncestor()
    Logger.debug('Scroll container found', { container: scrollContainer ? scrollContainer.className : 'viewport' })

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          Logger.debug('Intersection observed', { 
            isIntersecting: entry.isIntersecting, 
            hasMore: this.hasMoreValue, 
            loading: this.loadingValue 
          })
          if (entry.isIntersecting && this.hasMoreValue && !this.loadingValue) {
            Logger.debug('Loading more content...', { controller: this.constructor.name })
            this.loadMore()
          }
        })
      },
      {
        root: scrollContainer, // Use scrollable ancestor instead of viewport
        threshold: this.thresholdValue,
        rootMargin: this.rootMarginValue
      }
    )

    this.observer.observe(this.sentinelTarget)
    Logger.debug('Intersection observer setup complete', { controller: this.constructor.name })
  }

  /**
   * Find the scrollable ancestor element
   * Walks up the DOM tree to find the first parent with overflow-y: auto or scroll
   * @returns {HTMLElement|null} The scrollable container or null for viewport
   */
  findScrollableAncestor() {
    let element = this.element.parentElement
    
    while (element && element !== document.body) {
      // Check for known scrollable container classes first (faster check)
      if (element.classList.contains('map-tab-content') ||
          element.classList.contains('overflow-auto') ||
          element.classList.contains('overflow-y-auto')) {
        Logger.debug('Found scrollable container by class', { className: element.className })
        return element
      }
      
      const style = getComputedStyle(element)
      const overflowY = style.overflowY
      const maxHeight = style.maxHeight
      
      // Check for scrollable container by computed style
      if ((overflowY === 'auto' || overflowY === 'scroll') && maxHeight !== 'none') {
        Logger.debug('Found scrollable container by style', { 
          className: element.className, 
          overflowY, 
          maxHeight 
        })
        return element
      }
      
      element = element.parentElement
    }
    
    Logger.debug('No scrollable container found, using viewport', { controller: this.constructor.name })
    return null // Use viewport as default
  }

  /**
   * Load more content from the server
   */
  async loadMore() {
    if (this.loadingValue || !this.hasMoreValue || !this.urlValue) {
      return
    }

    // Module-level guard: prevent multiple concurrent loads for the same element.
    // When IntersectionObserver fires with multiple entries in one callback,
    // only the first entry proceeds; subsequent entries see loadingElements.has()
    // and return early before this.loadingValue is synchronously updated.
    if (this.constructor._loadingElements.has(this.element)) {
      return
    }

    this.constructor._loadingElements.add(this.element)

    try {
      this.setLoading(true)
      this.dispatch('loading', { detail: { page: this.pageValue } })

      const url = this.buildUrl()
      Logger.debug(`Loading page ${this.pageValue}`, { url })

      const response = await this.safeFetch(url, {
        headers: {
          'Accept': 'text/html',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const html = await response.text()
      const metadata = this.parseMetadata(response)

      this.appendContent(html)
      this.updateState(metadata)

      this.dispatch('loaded', { 
        detail: { 
          page: this.pageValue, 
          hasMore: this.hasMoreValue,
          totalCount: metadata.totalCount
        } 
      })

    } catch (error) {
      Logger.error('Failed to load more content', error, { controller: this.constructor.name })
      this.dispatch('error', { detail: { error: error.message } })
      this.showError(error.message)
    } finally {
      this.setLoading(false)
      // Defer cleanup to ensure all synchronous observer entries have been processed
      queueMicrotask(() => {
        this.constructor._loadingElements.delete(this.element)
      })
    }
  }

  /**
   * Build URL with current parameters
   */
  buildUrl() {
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set('page', this.pageValue)
    url.searchParams.set('per', this.perPageValue)
    return url.toString()
  }

  /**
   * Parse metadata from response headers
   */
  parseMetadata(response) {
    return {
      hasMore: response.headers.get('X-Has-More') === 'true',
      totalCount: parseInt(response.headers.get('X-Total-Count') || '0'),
      currentPage: parseInt(response.headers.get('X-Current-Page') || '1')
    }
  }

  /**
   * Append new content to the entries container
   */
  appendContent(html) {
    if (!this.hasEntriesTarget) {
      Logger.warn('No entries target found for appending content', { controller: this.constructor.name })
      return
    }

    // Use a template and table structure to properly parse table rows
    const template = document.createElement('template')
    template.innerHTML = html.trim()
    const content = template.content

    // A response that is the whole list again (the bids CRM's map lists render one partial for every page)
    // carries its own entries container: take its children, so neither a table's header row nor the frame
    // around a card grid is appended with them. A bare fragment keeps the old reading: its table rows, which
    // might be direct children or inside a tbody, else its top-level elements.
    let newContent = []
    const entries = content.querySelector('[data-infinite-scroll-target="entries"]')
    const rows = content.querySelectorAll('tr')
    if (entries) {
      newContent = Array.from(entries.children)
    } else if (rows.length > 0) {
      newContent = Array.from(rows)
    } else {
      newContent = Array.from(content.children)
    }

    if (newContent.length === 0) {
      Logger.warn('No content found in response', { controller: this.constructor.name })
      return
    }

    // Append to the target - if it's a tbody, just append the rows
    const target = this.entriesTarget
    newContent.forEach(element => {
      target.appendChild(element)
    })

    Logger.debug(`Appended ${newContent.length} items`, { tagName: target.tagName })
  }

  /**
   * Update controller state based on metadata
   */
  updateState(metadata) {
    this.pageValue = metadata.currentPage + 1
    this.hasMoreValue = metadata.hasMore

    if (!this.hasMoreValue) {
      this.hideSentinel()
    }
  }

  /**
   * Set loading state
   */
  setLoading(loading) {
    this.loadingValue = loading
    
    if (this.hasLoadingTarget) {
      if (loading) {
        this.loadingTarget.classList.remove('d-none')
      } else {
        this.loadingTarget.classList.add('d-none')
      }
    }
  }

  /**
   * Hide sentinel when no more content is available (CSP-compliant)
   */
  hideSentinel() {
    if (this.hasSentinelTarget) {
      this.sentinelTarget.classList.add('d-none')
    }
  }

  /**
   * Show error message (CSP-compliant — uses DOM API instead of innerHTML)
   */
  showError(message) {
    if (this.hasEntriesTarget) {
      const errorDiv = document.createElement('div')
      errorDiv.className = 'alert alert-danger mt-2'

      const icon = document.createElement('i')
      icon.className = 'fas fa-exclamation-triangle me-2'
      errorDiv.appendChild(icon)

      errorDiv.appendChild(document.createTextNode(`Failed to load more content: ${message} `))

      const retryBtn = document.createElement('button')
      retryBtn.type = 'button'
      retryBtn.className = 'btn btn-sm btn-outline-danger ms-2'
      retryBtn.textContent = 'Retry'
      retryBtn.setAttribute('data-action', 'click->infinite-scroll#loadMore')
      errorDiv.appendChild(retryBtn)

      this.entriesTarget.appendChild(errorDiv)
    }
  }

  /**
   * Reset the infinite scroll state (CSP-compliant)
   */
  reset() {
    Logger.debug('Resetting infinite scroll state', { controller: this.constructor.name })
    this.pageValue = 1
    this.hasMoreValue = true
    
    if (this.hasSentinelTarget) {
      this.sentinelTarget.classList.remove('d-none')
    }
    
    if (this.hasEntriesTarget) {
      // Clear existing content
      this.entriesTarget.innerHTML = ''
    }
    
    this.dispatch('reset', { detail: { controller: this } })
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    this.constructor._loadingElements.delete(this.element)
  }

  /**
   * Public method to manually trigger loading
   */
  loadMoreManually() {
    this.loadMore()
  }

  /**
   * Public method to check if currently loading
   */
  isLoading() {
    return this.loadingValue
  }

  /**
   * Public method to check if has more content
   */
  hasMoreContent() {
    return this.hasMoreValue
  }
}
